/*
|--------------------------------------------------------------------------
| Decorators for Resourceful Lucid Models
|--------------------------------------------------------------------------
|
| Decorators which are used to add the required metadata to Lucid models
| which have been extended with the Resourceful mixin.
|
*/

/*
|--------------------------------------------------------------------------
| NOTE FOR FUTURE MAINTAINERS
|--------------------------------------------------------------------------
|
| The `type` property for resourceful decorators (e.g., resourcefulColumn, resourcefulComputed)
| must always be a ResourcefulDataType instance (e.g., ResourcefulStringType(), ResourcefulNumberType(), etc.),
| not a string literal like 'string' or 'number'.
| This is required because the system expects a structured data type definition with validation,
| format, and other metadata, not just a primitive string. See src/private/data_types.ts for details.
|
*/

import { guards } from '@nhtio/lucid-resourceful/utils'
import {
  PrepareConsumeChainBuilder,
  DefaultPrepareFunction,
  DefaultConsumeFunction,
} from './prepare_consume_chain_builder'
import {
  LucidModel,
  ColumnOptions,
  ComputedOptions,
  DateColumnDecorator,
  DateTimeColumnDecorator,
} from '@adonisjs/lucid/types/model'
import {
  column as lucidColumn,
  computed as lucidComputed,
  belongsTo as lucidBelongsTo,
  hasOne as lucidHasOne,
  hasMany as lucidHasMany,
  manyToMany as lucidManyToMany,
  hasManyThrough as lucidHasManyThrough,
} from '@adonisjs/lucid/orm'
import {
  prepareString,
  prepareDate,
  prepareDateTime,
  prepareBinary,
  prepareNumber,
  prepareInteger,
  prepareBigint,
  prepareUnsignedint,
  prepareBoolean,
  prepareObject,
  prepareArray,
  consumeString,
  consumeDate,
  consumeDateTime,
  consumeBinary,
  consumeNumber,
  consumeInteger,
  consumeBigint,
  consumeUnsignedint,
  consumeBoolean,
  consumeObject,
  consumeArray,
} from './decorator_utils'
import {
  validateNormalizeAndCastDecoratorOptions,
  resourcefulColumnOptionsSchema,
  stringColumnOptionsSchema,
  dateColumnOptionsSchema,
  dateTimeColumnOptionsSchema,
  binaryColumnOptionsSchema,
  numberColumnOptionsSchema,
  integerColumnOptionsSchema,
  bigintColumnOptionsSchema,
  unsignedIntegerColumnOptionsSchema,
  booleanColumnOptionsSchema,
  objectColumnOptionsSchema,
  arrayColumnOptionsSchema,
  resourcefulComputedOptionsSchema,
  stringComputedOptionsSchema,
  dateComputedOptionsSchema,
  dateTimeComputedOptionsSchema,
  binaryComputedOptionsSchema,
  numberComputedOptionsSchema,
  integerComputedOptionsSchema,
  bigintComputedOptionsSchema,
  unsignedIntegerComputedOptionsSchema,
  booleanComputedOptionsSchema,
  objectComputedOptionsSchema,
  arrayComputedOptionsSchema,
  relationOptionsSchema,
  manyToManyRelationOptionsSchema,
  hasManyThroughRelationOptionsSchema,
} from './decorator_schemas'
import type {
  HasOne,
  ManyToMany,
  HasManyThrough,
  ManyToManyRelationOptions,
  RelationOptions,
  ThroughRelationOptions,
} from '@adonisjs/lucid/types/relations'
import type {
  ResourcefulColumnDefinition,
  ResourcefulComputedAccessorDefinition,
  ResourcefulRelationshipDefinition,
  AnySchema,
  StringSchema,
  DateSchema,
  BinarySchema,
  NumberSchema,
  BooleanSchema,
  ObjectSchema,
  ArraySchema,
  ResourcefulPropertySchema,
} from '@nhtio/lucid-resourceful/types'

/**
 * Options for date columns including autoCreate and autoUpdate flags.
 *
 * @example
 * ```ts
 * {
 *   autoCreate: true,
 *   autoUpdate: false,
 *   nullable: false,
 * }
 * ```
 */
export type DateColumnOptions = DataTypeColumnOptions & {
  autoCreate: boolean
  autoUpdate: boolean
}

/**
 * Options for relation decorators on related models.
 *
 * @example
 * ```ts
 * import { resourcefulBelongsTo } from '@nhtio/lucid-resourceful'
 *
 * class Post {
 *   @resourcefulBelongsTo(() => User, { foreignKey: 'user_id' })
 *   public user: User
 * }
 * ```
 */
export type RelatedModelRelationOptions<RelatedModel extends LucidModel> =
  | RelationOptions<RelatedModel, LucidModel, HasOne<RelatedModel, LucidModel>>
  | ManyToManyRelationOptions<ManyToMany<RelatedModel>>
  | Omit<
      ThroughRelationOptions<RelatedModel, LucidModel, HasManyThrough<RelatedModel>>,
      'throughModel'
    >

/**
 * Options for hasManyThrough relation decorators.
 *
 * @example
 * ```ts
 * import { resourcefulHasManyThrough } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulHasManyThrough([
 *     () => Role,
 *     () => UserRolePivot
 *   ], { foreignKey: 'user_id' })
 *   public roles: HasManyThrough<typeof Role>
 * }
 * ```
 */
export type HasManyThroughRelationOptions<RelatedModel extends LucidModel> = Omit<
  ThroughRelationOptions<RelatedModel, LucidModel, HasManyThrough<RelatedModel>>,
  'throughModel'
>

type ValidatorInput<Schema extends AnySchema = AnySchema> = Partial<DataTypeColumnOptions> &
  Partial<ResourcefulColumnDefinition<Schema>>
type ValidatorOutnput<Schema extends AnySchema = AnySchema> = DataTypeColumnOptions &
  ResourcefulColumnDefinition<Schema>

interface ColumnOptionsWithDate extends ColumnOptions {
  autoCreate: boolean
  autoUpdate: boolean
}

const extractLucidOptionsForLucidDecorator = <
  LucidDecoratorOptionsType,
  InputOptionsType extends LucidDecoratorOptionsType = LucidDecoratorOptionsType,
>(
  options: InputOptionsType,
  keys: Array<keyof LucidDecoratorOptionsType>,
  overrides?: Partial<LucidDecoratorOptionsType>
): Partial<LucidDecoratorOptionsType> => {
  const extracted: Partial<LucidDecoratorOptionsType> = {}
  Object.entries(options as {}).forEach((entry) => {
    const [key, value] = entry as [keyof InputOptionsType, InputOptionsType[keyof InputOptionsType]]
    if (keys.includes(key as keyof LucidDecoratorOptionsType)) {
      const k = key as keyof LucidDecoratorOptionsType
      const v = value as unknown as LucidDecoratorOptionsType[keyof LucidDecoratorOptionsType]
      extracted[k] = v
    }
  })
  if (guards.isObject(overrides)) {
    Object.assign(extracted, overrides)
  }
  return extracted
}

const extractLucidOptionsForLucidColumnDecorator = (
  options: Partial<ColumnOptionsWithDate>,
  overrides?: Partial<ColumnOptionsWithDate>
) => {
  return extractLucidOptionsForLucidDecorator<Partial<ColumnOptionsWithDate>>(
    options,
    [
      'columnName',
      'serializeAs',
      'isPrimary',
      'meta',
      'serialize',
      'prepare',
      'consume',
      'autoCreate',
      'autoUpdate',
    ],
    overrides
  )
}

const extractLucidOptionsForLucidComputedDecorator = (
  options: Partial<ComputedOptions>,
  overrides?: Partial<ComputedOptions>
) => {
  return extractLucidOptionsForLucidDecorator<Partial<ComputedOptions>>(
    options,
    ['serializeAs', 'meta'],
    overrides
  )
}

const extractLucidOptionsForLucidRelatedModelDecorator = <RelatedModel extends LucidModel>(
  options: Partial<RelatedModelRelationOptions<RelatedModel>>,
  overrides?: Partial<RelatedModelRelationOptions<RelatedModel>>
) => {
  return extractLucidOptionsForLucidDecorator<Partial<RelatedModelRelationOptions<RelatedModel>>>(
    options,
    [
      'localKey',
      'foreignKey',
      'serializeAs',
      'onQuery',
      'meta',
      'pivotTable',
      'localKey',
      'pivotForeignKey',
      'relatedKey',
      'pivotRelatedForeignKey',
      'pivotColumns',
      'pivotTimestamps',
      'throughLocalKey',
      'throughForeignKey',
    ] as any,
    overrides
  )
}

const extractLucidOptionsForLucidHasManyThroughDecorator = <RelatedModel extends LucidModel>(
  options: Partial<HasManyThroughRelationOptions<RelatedModel>>,
  overrides?: Partial<HasManyThroughRelationOptions<RelatedModel>>
) => {
  return extractLucidOptionsForLucidDecorator<Partial<HasManyThroughRelationOptions<RelatedModel>>>(
    options,
    [
      'localKey',
      'foreignKey',
      'serializeAs',
      'onQuery',
      'meta',
      'pivotTable',
      'localKey',
      'pivotForeignKey',
      'relatedKey',
      'pivotRelatedForeignKey',
      'pivotColumns',
      'pivotTimestamps',
      'throughLocalKey',
      'throughForeignKey',
    ] as any,
    overrides
  )
}

const handleResourcefulColumn = <Schema extends AnySchema = ResourcefulPropertySchema>(config: {
  decoratorName: string
  target: any
  propertyKey: string
  decoratorOption: Partial<ResourcefulColumnDefinition<Schema>>
  decoratorOptionsValidationSchema: ObjectSchema
  lucidDecoratorFunction: typeof lucidColumn | DateColumnDecorator | DateTimeColumnDecorator
  overrides?: Partial<ColumnOptions>
  defaultPrepare?: DefaultPrepareFunction
  defaultConsume?: DefaultConsumeFunction
}) => {
  const {
    decoratorName,
    target,
    propertyKey,
    decoratorOption,
    decoratorOptionsValidationSchema,
    overrides = {},
    lucidDecoratorFunction,
    defaultPrepare,
    defaultConsume,
  } = config
  /*
    |--------------------------------------------------------------------------
    | Fields which are auto created/updated are always nullable
    |--------------------------------------------------------------------------
    */
  decoratorOption.nullable =
    decoratorOption.nullable === true ||
    ('autoCreate' in decoratorOption && decoratorOption.autoCreate === true) ||
    ('autoUpdate' in decoratorOption && decoratorOption.autoUpdate === true) ||
    false
  /*
    |--------------------------------------------------------------------------
    | - If the name of the field is `createdAt` or `created_at` and `immutable`
    | is not explicitly set, set `immutable` to true
    | - If the field is autoCreated. but not autoUpdated and `immutable` is 
    | not explicitly set, set `immutable` to true
    | - If the property key is the model's primary key and `immutable` is not
    | explicitly set, set `immutable` to true
    |--------------------------------------------------------------------------
    | This is a helper to reduce boilerplate code and make it less likely that
    | fields which developers commonly intend to be immutable are accidentally
    | made mutable.
    */
  if (
    !('immutable' in decoratorOption) &&
    (propertyKey === 'createdAt' ||
      propertyKey === 'created_at' ||
      ('autoCreate' in decoratorOption &&
        decoratorOption.autoCreate === true &&
        (!('autoUpdate' in decoratorOption) || decoratorOption.autoUpdate === false)) ||
      ('isPrimary' in decoratorOption && decoratorOption.isPrimary === true))
  ) {
    decoratorOption.immutable = true
  } else {
    /*
    |--------------------------------------------------------------------------
    | Ensure that immutability is explicitly set to false if not true
    |--------------------------------------------------------------------------
    */
    decoratorOption.immutable = decoratorOption.immutable || false
  }
  const opts = validateNormalizeAndCastDecoratorOptions<
    Partial<ResourcefulColumnDefinition<Schema>>,
    ResourcefulColumnDefinition<Schema>
  >(propertyKey, decoratorName, decoratorOptionsValidationSchema, {
    ...decoratorOption,
    propertyKey,
  })
  const customPrepare = opts.prepare
  const customConsume = opts.consume
  const nullable = opts.nullable || false

  // Create chained functions using PrepareConsumeChainBuilder
  const chainedPrepare = PrepareConsumeChainBuilder.chainPrepare(
    propertyKey,
    nullable,
    defaultPrepare,
    customPrepare
  )
  const chainedConsume = PrepareConsumeChainBuilder.chainConsume(
    propertyKey,
    nullable,
    defaultConsume,
    customConsume
  )

  // Merge DataTypeColumnOptions for Lucid, excluding prepare/consume from opts
  const lucidOptions = extractLucidOptionsForLucidColumnDecorator(
    opts as unknown as Partial<ColumnOptions>,
    {
      ...overrides,
      ...(chainedPrepare && { prepare: chainedPrepare }),
      ...(chainedConsume && { consume: chainedConsume }),
    }
  )

  lucidDecoratorFunction(lucidOptions)(target, propertyKey)

  const map = ensureOwnResourcefulMap(target, '$resourcefulColumns')
  map.set(propertyKey, opts)
}

const handleResourcefulComputed = <Schema extends AnySchema = ResourcefulPropertySchema>(config: {
  decoratorName: string
  target: any
  propertyKey: string
  decoratorOption: Partial<ResourcefulComputedAccessorDefinition<Schema>>
  decoratorOptionsValidationSchema: ObjectSchema
}) => {
  const { decoratorName, target, propertyKey, decoratorOption, decoratorOptionsValidationSchema } =
    config
  const opts = validateNormalizeAndCastDecoratorOptions<
    Partial<ResourcefulComputedAccessorDefinition<Schema>>,
    ResourcefulComputedAccessorDefinition<Schema>
  >(propertyKey, decoratorName, decoratorOptionsValidationSchema, {
    ...decoratorOption,
    propertyKey,
  })
  // Merge DataTypeComputedOptions for Lucid
  const lucidOptions = extractLucidOptionsForLucidComputedDecorator(
    opts as unknown as Partial<ComputedOptions>
  )

  lucidComputed(lucidOptions)(target, propertyKey)

  const map = ensureOwnResourcefulMap(target, '$resourcefulComputedAccessors')
  map.set(propertyKey, opts)
}

function ensureOwnResourcefulMap(target: any, mapKey: string) {
  const ctor = target.constructor
  if (!Object.prototype.hasOwnProperty.call(ctor, mapKey)) {
    ctor[mapKey] = new Map()
  }
  return ctor[mapKey]
}

export type DataTypeColumnOptions = ColumnOptions

export type DataTypeComputedOptions = Omit<ComputedOptions, 'prepare' | 'consume'>

/**
 * Decorator to define a resourceful column on a Lucid model property.
 * Applies validation, metadata, and Lucid column options.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulStringType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulColumn({ type: ResourcefulStringType({ minLength: 1, maxLength: 100 }), nullable: false })
 *   public name: string
 * }
 * ```
 *
 * @param options - Resourceful column options and Lucid column options.
 * @returns Property decorator function.
 */
export function resourcefulColumn<Schema extends AnySchema = ResourcefulPropertySchema>(
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<Schema>> = {}
) {
  const decoratorName = 'resourcefulColumn'
  const decoratorOptionsValidationSchema = resourcefulColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    const opts = validateNormalizeAndCastDecoratorOptions<
      ValidatorInput<Schema>,
      ValidatorOutnput<Schema>
    >(propertyKey, decoratorName, decoratorOptionsValidationSchema, { ...options, propertyKey })
    const lucidOptions = extractLucidOptionsForLucidColumnDecorator(opts)

    lucidColumn(lucidOptions)(target, propertyKey)

    const map = ensureOwnResourcefulMap(target, '$resourcefulColumns')
    map.set(propertyKey, opts)
  }
}

/**
 * Decorator to define a resourceful string column on a Lucid model property.
 * Includes prepare and consume functions for string type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulStringType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulColumn.string({ type: ResourcefulStringType({ minLength: 3 }), nullable: false })
 *   public email: string
 * }
 * ```
 *
 * @param options - Resourceful string column options.
 * @returns Property decorator function.
 */
resourcefulColumn.string = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<StringSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.string'
  const decoratorOptionsValidationSchema = stringColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareString as DefaultPrepareFunction,
      defaultConsume: consumeString as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful date column on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulDateType } from '@nhtio/lucid-resourceful'
 *
 * class Event {
 *   @resourcefulColumn.date({ type: ResourcefulDateType(), autoCreate: true, autoUpdate: false })
 *   public startDate: Date
 * }
 * ```
 *
 * @param options - Resourceful date column options.
 * @returns Property decorator function.
 */
resourcefulColumn.date = function (
  options: Partial<DateColumnOptions> & Partial<ResourcefulColumnDefinition<DateSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.date'
  const decoratorOptionsValidationSchema = dateColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn.date,
      defaultPrepare: prepareDate as DefaultPrepareFunction,
      defaultConsume: consumeDate as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful dateTime column on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulDateTimeType } from '@nhtio/lucid-resourceful'
 *
 * class Event {
 *   @resourcefulColumn.dateTime({ type: ResourcefulDateTimeType(), autoCreate: true, autoUpdate: true })
 *   public updatedAt: Date
 * }
 * ```
 *
 * @param options - Resourceful dateTime column options.
 * @returns Property decorator function.
 */
resourcefulColumn.dateTime = function (
  options: Partial<DateColumnOptions> & Partial<ResourcefulColumnDefinition<DateSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.dateTime'
  const decoratorOptionsValidationSchema = dateTimeColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn.dateTime,
      defaultPrepare: prepareDateTime as DefaultPrepareFunction,
      defaultConsume: consumeDateTime as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful binary column on a Lucid model property.
 * Includes prepare and consume functions for binary type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulBinaryType } from '@nhtio/lucid-resourceful'
 *
 * class File {
 *   @resourcefulColumn.binary({ type: ResourcefulBinaryType(), nullable: true })
 *   public data: Buffer
 * }
 * ```
 *
 * @param options - Resourceful binary column options.
 * @returns Property decorator function.
 */
resourcefulColumn.binary = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<BinarySchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.binary'
  const decoratorOptionsValidationSchema = binaryColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareBinary as DefaultPrepareFunction,
      defaultConsume: consumeBinary as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful number column on a Lucid model property.
 * Includes prepare and consume functions for number type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulNumberType } from '@nhtio/lucid-resourceful'
 *
 * class Product {
 *   @resourcefulColumn.number({ type: ResourcefulNumberType(), nullable: false })
 *   public price: number
 * }
 * ```
 *
 * @param options - Resourceful number column options.
 * @returns Property decorator function.
 */
resourcefulColumn.number = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.number'
  const decoratorOptionsValidationSchema = numberColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareNumber as DefaultPrepareFunction,
      defaultConsume: consumeNumber as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful integer column on a Lucid model property.
 * Includes prepare and consume functions for integer type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulIntegerType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulColumn.integer({ type: ResourcefulIntegerType(), nullable: false })
 *   public age: number
 * }
 * ```
 *
 * @param options - Resourceful integer column options.
 * @returns Property decorator function.
 */
resourcefulColumn.integer = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.integer'
  const decoratorOptionsValidationSchema = integerColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareInteger as DefaultPrepareFunction,
      defaultConsume: consumeInteger as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful bigint column on a Lucid model property.
 * Includes prepare and consume functions for bigint type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulBigintType } from '@nhtio/lucid-resourceful'
 *
 * class Transaction {
 *   @resourcefulColumn.bigint({ type: ResourcefulBigintType(), nullable: false })
 *   public bigValue: bigint
 * }
 * ```
 *
 * @param options - Resourceful bigint column options.
 * @returns Property decorator function.
 */
resourcefulColumn.bigint = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.bigint'
  const decoratorOptionsValidationSchema = bigintColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareBigint as DefaultPrepareFunction,
      defaultConsume: consumeBigint as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful unsigned integer column on a Lucid model property.
 * Includes prepare and consume functions for unsigned integer type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulUnsignedIntegerType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulColumn.unsignedint({ type: ResourcefulUnsignedIntegerType(), nullable: false })
 *   public positiveCount: number
 * }
 * ```
 *
 * @param options - Resourceful unsigned integer column options.
 * @returns Property decorator function.
 */
resourcefulColumn.unsignedint = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.unsignedint'
  const decoratorOptionsValidationSchema = unsignedIntegerColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareUnsignedint as DefaultPrepareFunction,
      defaultConsume: consumeUnsignedint as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful boolean column on a Lucid model property.
 * Includes prepare and consume functions for boolean type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulBooleanType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulColumn.boolean({ type: ResourcefulBooleanType(), nullable: false })
 *   public isActive: boolean
 * }
 * ```
 *
 * @param options - Resourceful boolean column options.
 * @returns Property decorator function.
 */
resourcefulColumn.boolean = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<BooleanSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.boolean'
  const decoratorOptionsValidationSchema = booleanColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareBoolean as DefaultPrepareFunction,
      defaultConsume: consumeBoolean as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful object column on a Lucid model property.
 * Includes prepare and consume functions for object type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulObjectType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulColumn.object({ type: ResourcefulObjectType(), nullable: true })
 *   public metadata: object
 * }
 * ```
 *
 * @param options - Resourceful object column options.
 * @returns Property decorator function.
 */
resourcefulColumn.object = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<ObjectSchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.object'
  const decoratorOptionsValidationSchema = objectColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareObject as DefaultPrepareFunction,
      defaultConsume: consumeObject as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful array column on a Lucid model property.
 * Includes prepare and consume functions for array type.
 *
 * @example
 * ```ts
 * import { resourcefulColumn, ResourcefulArrayType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulColumn.array({ type: ResourcefulArrayType({ items: { type: 'string' } }), nullable: true })
 *   public tags: any[]
 * }
 * ```
 *
 * @param options - Resourceful array column options.
 * @returns Property decorator function.
 */
resourcefulColumn.array = function (
  options: Partial<ColumnOptions> & Partial<ResourcefulColumnDefinition<ArraySchema>> = {}
) {
  const decoratorName = 'resourcefulColumn.array'
  const decoratorOptionsValidationSchema = arrayColumnOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulColumn({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
      lucidDecoratorFunction: lucidColumn,
      defaultPrepare: prepareArray as DefaultPrepareFunction,
      defaultConsume: consumeArray as DefaultConsumeFunction,
    })
  }
}

/**
 * Decorator to define a resourceful computed accessor on a Lucid model property.
 * Applies validation, metadata, and Lucid computed options.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulStringType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulComputed({ type: ResourcefulStringType({ minLength: 1, maxLength: 100 }) })
 *   public get fullName() {
 *     return `${this.firstName} ${this.lastName}`
 *   }
 * }
 * ```
 *
 * @param options - Resourceful computed accessor options and Lucid computed options.
 * @returns Property decorator function.
 */
export function resourcefulComputed<Schema extends AnySchema = ResourcefulPropertySchema>(
  options: Partial<ComputedOptions> & Partial<ResourcefulComputedAccessorDefinition<Schema>> = {}
) {
  const decoratorName = 'resourcefulComputed'
  const decoratorOptionsValidationSchema = resourcefulComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    const opts = validateNormalizeAndCastDecoratorOptions<
      ValidatorInput<Schema>,
      ValidatorOutnput<Schema>
    >(propertyKey, decoratorName, decoratorOptionsValidationSchema, { ...options, propertyKey })
    const lucidOptions = extractLucidOptionsForLucidComputedDecorator(opts)

    lucidComputed(lucidOptions)(target, propertyKey)

    const map = ensureOwnResourcefulMap(target, '$resourcefulComputedAccessors')
    map.set(propertyKey, opts)
  }
}

/**
 * Decorator to define a resourceful string computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulStringType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulComputed.string({ type: ResourcefulStringType({ minLength: 3 }) })
 *   public get displayName() {
 *     return this.name.toUpperCase()
 *   }
 * }
 * ```
 *
 * @param options - Resourceful string computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.string = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<StringSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.string'
  const decoratorOptionsValidationSchema = stringComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful date computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulDateType } from '@nhtio/lucid-resourceful'
 *
 * class Event {
 *   @resourcefulComputed.date({ type: ResourcefulDateType() })
 *   public get eventDate() {
 *     return this._eventDate
 *   }
 * }
 * ```
 *
 * @param options - Resourceful date computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.date = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<DateSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.date'
  const decoratorOptionsValidationSchema = dateComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful dateTime computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulDateTimeType } from '@nhtio/lucid-resourceful'
 *
 * class Event {
 *   @resourcefulComputed.dateTime({ type: ResourcefulDateTimeType() })
 *   public get updatedAt() {
 *     return this._updatedAt
 *   }
 * }
 * ```
 *
 * @param options - Resourceful dateTime computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.dateTime = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<DateSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.dateTime'
  const decoratorOptionsValidationSchema = dateTimeComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful binary computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulBinaryType } from '@nhtio/lucid-resourceful'
 *
 * class File {
 *   @resourcefulComputed.binary({ type: ResourcefulBinaryType() })
 *   public get fileData() {
 *     return this._fileData
 *   }
 * }
 * ```
 *
 * @param options - Resourceful binary computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.binary = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<BinarySchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.binary'
  const decoratorOptionsValidationSchema = binaryComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful number computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulNumberType } from '@nhtio/lucid-resourceful'
 *
 * class Product {
 *   @resourcefulComputed.number({ type: ResourcefulNumberType() })
 *   public get discountedPrice() {
 *     return this.price * 0.9
 *   }
 * }
 * ```
 *
 * @param options - Resourceful number computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.number = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.number'
  const decoratorOptionsValidationSchema = numberComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful integer computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulIntegerType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulComputed.integer({ type: ResourcefulIntegerType() })
 *   public get roundedAge() {
 *     return Math.floor(this.age)
 *   }
 * }
 * ```
 *
 * @param options - Resourceful integer computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.integer = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.integer'
  const decoratorOptionsValidationSchema = integerComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful bigint computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulBigintType } from '@nhtio/lucid-resourceful'
 *
 * class Transaction {
 *   @resourcefulComputed.bigint({ type: ResourcefulBigintType() })
 *   public get bigValue() {
 *     return this._bigValue
 *   }
 * }
 * ```
 *
 * @param options - Resourceful bigint computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.bigint = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.bigint'
  const decoratorOptionsValidationSchema = bigintComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful unsigned integer computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulUnsignedIntegerType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulComputed.unsignedint({ type: ResourcefulUnsignedIntegerType() })
 *   public get positiveCount() {
 *     return this._positiveCount
 *   }
 * }
 * ```
 *
 * @param options - Resourceful unsigned integer computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.unsignedint = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<NumberSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.unsignedint'
  const decoratorOptionsValidationSchema = unsignedIntegerComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful boolean computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulBooleanType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulComputed.boolean({ type: ResourcefulBooleanType() })
 *   public get isActive() {
 *     return this._isActive
 *   }
 * }
 * ```
 *
 * @param options - Resourceful boolean computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.boolean = function (
  options: Partial<DataTypeComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<BooleanSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.boolean'
  const decoratorOptionsValidationSchema = booleanComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful object computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulObjectType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulComputed.object({ type: ResourcefulObjectType() })
 *   public get metadata() {
 *     return this._metadata
 *   }
 * }
 * ```
 *
 * @param options - Resourceful object computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.object = function (
  options: Partial<ComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<ObjectSchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.object'
  const decoratorOptionsValidationSchema = objectComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful array computed accessor on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulComputed, ResourcefulArrayType } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulComputed.array({ type: ResourcefulArrayType({ items: { type: 'string' } }) })
 *   public get tags() {
 *     return this._tags
 *   }
 * }
 * ```
 *
 * @param options - Resourceful array computed accessor options.
 * @returns Property decorator function.
 */
resourcefulComputed.array = function (
  options: Partial<ComputedOptions> &
    Partial<ResourcefulComputedAccessorDefinition<ArraySchema>> = {}
) {
  const decoratorName = 'resourcefulComputed.array'
  const decoratorOptionsValidationSchema = arrayComputedOptionsSchema

  return function (target: any, propertyKey: string) {
    handleResourcefulComputed({
      decoratorName,
      target,
      propertyKey,
      decoratorOption: options,
      decoratorOptionsValidationSchema,
    })
  }
}

/**
 * Decorator to define a resourceful belongsTo relationship on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulBelongsTo } from '@nhtio/lucid-resourceful'
 *
 * class Post {
 *   @resourcefulBelongsTo(() => User, { foreignKey: 'user_id' })
 *   public user: User
 * }
 * ```
 *
 * @param model - Function returning the related model class.
 * @param options - Relationship options and resourceful relationship definition.
 * @returns Property decorator function.
 */
export function resourcefulBelongsTo<RelatedModel extends LucidModel>(
  model: () => RelatedModel,
  options: Partial<RelationOptions<RelatedModel, LucidModel, HasOne<RelatedModel, LucidModel>>> &
    Partial<ResourcefulRelationshipDefinition> = {}
) {
  return function (target: any, propertyKey: string) {
    const opts = validateNormalizeAndCastDecoratorOptions<any, any>(
      propertyKey,
      'resourcefulBelongsTo',
      relationOptionsSchema,
      { ...options, propertyKey, relatedModel: model }
    )
    const lucidOptions = extractLucidOptionsForLucidRelatedModelDecorator(opts)
    lucidBelongsTo(model, lucidOptions as any)(target, propertyKey)
    const map = ensureOwnResourcefulMap(target, '$resourcefulRelationships')
    map.set(propertyKey, opts)
  }
}

/**
 * Decorator to define a resourceful hasOne relationship on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulHasOne } from '@nhtio/lucid-resourceful'
 *
 * class UserProfile {
 *   @resourcefulHasOne(() => User, { foreignKey: 'user_id' })
 *   public user: UserProfile
 * }
 * ```
 *
 * @param model - Function returning the related model class.
 * @param options - Relationship options and resourceful relationship definition.
 * @returns Property decorator function.
 */
export function resourcefulHasOne<RelatedModel extends LucidModel>(
  model: () => RelatedModel,
  options: Partial<RelationOptions<RelatedModel, LucidModel, HasOne<RelatedModel, LucidModel>>> &
    Partial<ResourcefulRelationshipDefinition> = {}
) {
  return function (target: any, propertyKey: string) {
    const opts = validateNormalizeAndCastDecoratorOptions<any, any>(
      propertyKey,
      'resourcefulHasOne',
      relationOptionsSchema,
      { ...options, propertyKey, relatedModel: model }
    )
    const lucidOptions = extractLucidOptionsForLucidRelatedModelDecorator(opts)
    lucidHasOne(model, lucidOptions as any)(target, propertyKey)
    const map = ensureOwnResourcefulMap(target, '$resourcefulRelationships')
    map.set(propertyKey, opts)
  }
}

/**
 * Decorator to define a resourceful hasMany relationship on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulHasMany } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulHasMany(() => Post, { foreignKey: 'user_id' })
 *   public posts: HasMany<typeof Post>
 * }
 * ```
 *
 * @param model - Function returning the related model class.
 * @param options - Relationship options and resourceful relationship definition.
 * @returns Property decorator function.
 */
export function resourcefulHasMany<RelatedModel extends LucidModel>(
  model: () => RelatedModel,
  options: Partial<RelationOptions<RelatedModel, LucidModel, HasOne<RelatedModel, LucidModel>>> &
    Partial<ResourcefulRelationshipDefinition> = {}
) {
  return function (target: any, propertyKey: string) {
    const opts = validateNormalizeAndCastDecoratorOptions<any, any>(
      propertyKey,
      'resourcefulHasMany',
      relationOptionsSchema,
      { ...options, propertyKey, relatedModel: model }
    )
    const lucidOptions = extractLucidOptionsForLucidRelatedModelDecorator(opts)
    lucidHasMany(model, lucidOptions as any)(target, propertyKey)
    const map = ensureOwnResourcefulMap(target, '$resourcefulRelationships')
    map.set(propertyKey, opts)
  }
}

/**
 * Decorator to define a resourceful manyToMany relationship on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulManyToMany } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulManyToMany(() => Role, { pivotTable: 'role_user' })
 *   public roles: ManyToMany<typeof Role>
 * }
 * ```
 *
 * @param model - Function returning the related model class.
 * @param options - ManyToMany relationship options and resourceful relationship definition.
 * @returns Property decorator function.
 */
export function resourcefulManyToMany<RelatedModel extends LucidModel>(
  model: () => RelatedModel,
  options: Partial<ManyToManyRelationOptions<ManyToMany<RelatedModel>>> &
    Partial<ResourcefulRelationshipDefinition> = {}
) {
  return function (target: any, propertyKey: string) {
    const opts = validateNormalizeAndCastDecoratorOptions<any, any>(
      propertyKey,
      'resourcefulManyToMany',
      manyToManyRelationOptionsSchema,
      { ...options, propertyKey, relatedModel: model }
    )
    const lucidOptions = extractLucidOptionsForLucidRelatedModelDecorator(opts)
    lucidManyToMany(model, lucidOptions as any)(target, propertyKey)
    const map = ensureOwnResourcefulMap(target, '$resourcefulRelationships')
    map.set(propertyKey, opts)
  }
}

/**
 * Decorator to define a resourceful hasManyThrough relationship on a Lucid model property.
 *
 * @example
 * ```ts
 * import { resourcefulHasManyThrough } from '@nhtio/lucid-resourceful'
 *
 * class User {
 *   @resourcefulHasManyThrough([
 *     () => Role,
 *     () => UserRolePivot
 *   ], { foreignKey: 'user_id' })
 *   public roles: HasManyThrough<typeof Role>
 * }
 * ```
 *
 * @param model - Tuple of functions returning the related model and through model classes.
 * @param options - HasManyThrough relationship options and resourceful relationship definition.
 * @returns Property decorator function.
 */
export function resourcefulHasManyThrough<RelatedModel extends LucidModel>(
  model: [() => RelatedModel, () => LucidModel],
  options: Partial<HasManyThroughRelationOptions<RelatedModel>> &
    Partial<ResourcefulRelationshipDefinition> = {}
) {
  return function (target: any, propertyKey: string) {
    const opts = validateNormalizeAndCastDecoratorOptions<any, any>(
      propertyKey,
      'resourcefulHasManyThrough',
      hasManyThroughRelationOptionsSchema,
      { ...options, propertyKey, relatedModel: model[0] }
    )
    const lucidOptions = extractLucidOptionsForLucidHasManyThroughDecorator(opts)
    lucidHasManyThrough(model, lucidOptions as any)(target, propertyKey)
    const map = ensureOwnResourcefulMap(target, '$resourcefulRelationships')
    map.set(propertyKey, opts)
  }
}
